import { ReadonlyURLSearchParams } from 'next/navigation'

import { AnalyticsTagType, CategoryLevel, SearchItemType, SearchItemWithImageViewModel } from '@/types/api'
import { CombinationSlugPageViewModel } from '@/types/cml-api'

import { PRODUCT_PAGE_ROUTE } from '@/app/(pdp)/constants'
import {
    isBrandCategoryMetas,
    isBrandMetas,
    isCategoryMetas,
    isCombinationMetas,
    isTagMetas,
} from '@/app/(plp)/_utils/type-guards'
import { PLPBasicMetasProps, PLPPages } from '@/app/(plp)/types'
import { getServiceUrl, selectAPIService } from '@/app/_utils/select-api-service'

import { PRODUCT_QUERY_PARAMS } from '../constants'

export const getProductURL = (slug: string) => `/products/${slug}`

export const getCurrentPageURLString = (
    searchParams: ReadonlyURLSearchParams | URLSearchParams,
    baseURL: Location = window.location
) => {
    return `${baseURL.origin}${baseURL.pathname}?${searchParams.toString()}${baseURL.hash}`
}

export const getBaseURL = ({
    page,
    basicMetas,
    slug,
    query,
}: {
    page: PLPPages
    basicMetas?: PLPBasicMetasProps | CombinationSlugPageViewModel
    slug?: string
    query?: string
}) => {
    switch (page) {
        case PLPPages.Brands: {
            const brandSlug = isBrandMetas(page, basicMetas) ? basicMetas.slug : slug || ''
            return `${page}${brandSlug ? '/' : ''}${brandSlug}`
        }
        case PLPPages.Tags: {
            const tagSlug = isTagMetas(page, basicMetas) ? basicMetas.slug : slug || ''
            return `${page}${tagSlug ? '/' : ''}${tagSlug}`
        }
        case PLPPages.Facets: {
            const facetSlug = isCombinationMetas(page, basicMetas) ? basicMetas.slug : slug || ''
            return `${page}${facetSlug ? '/' : ''}${facetSlug}`
        }
        case PLPPages.Categories: {
            const categoryMetas = isCategoryMetas(page, basicMetas) ? basicMetas : undefined
            return `${page}${getBreadcrumbURI(categoryMetas?.breadcrumb)}`
        }
        case PLPPages.Search:
            return `${page}` + (query ? `?query=${query}` : '')
        case PLPPages.BrandCategoriesCombination: {
            const brandCategoryMetas = isCombinationMetas(page, basicMetas) ? basicMetas : undefined
            return `${page}${brandCategoryMetas?.slug}`
        }
        default:
            return '/'
    }
}

export const getBreadcrumbURI = (b: CategoryLevel[] | null | undefined) => {
    const result = b?.map(({ slug }) => slug)?.join('/')
    return result ? '/' + result : ''
}

export const getSearchResultItemURL = (item: SearchItemWithImageViewModel) => {
    switch (item.type) {
        case SearchItemType.Brand:
            return `/${getBaseURL({ page: PLPPages.Brands })}/${item.slug}`
        case SearchItemType.Category:
            return `/${getBaseURL({ page: PLPPages.Categories })}/${item.slug}`
        case SearchItemType.Product:
            return `/${PRODUCT_PAGE_ROUTE}/${item.slug}`
        default:
            return item.slug || '/'
    }
}

export const getPriceRangeURL = (page?: PLPPages, basicMetas?: PLPBasicMetasProps) => {
    const apiService = selectAPIService()
    const baseUrl = getServiceUrl(apiService.ntl)

    if (!page) return null

    switch (page) {
        case PLPPages.Search:
            return `${baseUrl}/catalog/price-ranges/search?analytics_tag=${AnalyticsTagType.PriceRangesPLP}`
        case PLPPages.Brands: {
            const slug = isBrandMetas(page, basicMetas) ? basicMetas?.slug : undefined

            if (!slug) return null

            return `${baseUrl}/catalog/price-ranges/brand/${slug}?analytics_tag=${AnalyticsTagType.PriceRangesPLP}`
        }
        case PLPPages.Combination: {
            const slug = isBrandCategoryMetas(page, basicMetas) ? basicMetas.brand?.slug : undefined

            if (!slug) return null

            return `${baseUrl}/catalog/price-ranges/brand/${slug}?analytics_tag=${AnalyticsTagType.PriceRangesPLP}`
        }
        case PLPPages.BrandCategoriesCombination: {
            const slug = isCombinationMetas(page, basicMetas) ? basicMetas.brand?.slug : undefined

            if (!slug) return null

            return `${baseUrl}/catalog/price-ranges/brand/${slug}?analytics_tag=${AnalyticsTagType.PriceRangesPLP}`
        }
        case PLPPages.Tags: {
            const slug = isTagMetas(page, basicMetas) ? basicMetas?.slug : undefined

            if (!slug) return null

            return `${baseUrl}/catalog/price-ranges/tag/${slug}?analytics_tag=${AnalyticsTagType.PriceRangesPLP}`
        }
        case PLPPages.Categories: {
            const breadcrumb = isCategoryMetas(page, basicMetas) ? basicMetas?.breadcrumb : undefined

            if (!breadcrumb?.length) return null

            return `${baseUrl}/catalog/price-ranges/category${getBreadcrumbURI(breadcrumb)}?analytics_tag=${AnalyticsTagType.PriceRangesPLP}`
        }
        case PLPPages.Facets: {
            const slug = isCombinationMetas(page, basicMetas) ? basicMetas?.slug : undefined

            if (!slug) return null

            return `${baseUrl}/catalog/price-ranges/category/${slug}?analytics_tag=${AnalyticsTagType.PriceRangesPLP}`
        }
        default:
            return null
    }
}

/**
 * checks if parameters of u1 and u2 have changed or not
 * @param u1 string of query params. e.g.: a=1&b=2&sort=MostExpensive&cat=blush&cat=makeup&page_number=2
 * @param u2 string of query params. e.g.: a=1&b=2&sort=MostExpensive&cat=blush&cat=makeup&page_number=2
 * @returns  an object with the following keys:
 *           {
 *              sort,
 *              page_number,
 *              otherParams
 *            }
 */
export const urlSearchParamsDiffer = (u1: string, u2: string) => {
    const params1 = new URLSearchParams(u1)
    const params2 = new URLSearchParams(u2)

    // for checking size should not consider PAGE_NUMBER and SORT_BY
    const otherParamsLength1 = Array.from(params1.keys()).filter(
        (key) => key !== PRODUCT_QUERY_PARAMS.PAGE_NUMBER && key !== PRODUCT_QUERY_PARAMS.SORT_BY
    ).length
    const otherParamsLength2 = Array.from(params2.keys()).filter(
        (key) => key !== PRODUCT_QUERY_PARAMS.PAGE_NUMBER && key !== PRODUCT_QUERY_PARAMS.SORT_BY
    ).length

    let otherParamsDiffer = otherParamsLength1 !== otherParamsLength2

    !otherParamsDiffer &&
        params1.forEach((value, key) => {
            if (key === PRODUCT_QUERY_PARAMS.PAGE_NUMBER || key === PRODUCT_QUERY_PARAMS.SORT_BY) return

            // if a param like 'cat' which may be repeated and have more than one value differs
            const value1 = params1.getAll(key)
            const value2 = params2.getAll(key)
            if (value1.length !== value2.length) {
                otherParamsDiffer = true
                return
            }

            // if only one value, compare strings normally
            if (value2.length === 1) {
                otherParamsDiffer ||= value !== params2.get(key)
                return
            } else {
                // here we know value1.size is === value2.size and they have more than one value, so we have to check if the value exists in value2 or not
                otherParamsDiffer ||= !value2.includes(value)
            }
        })

    return {
        otherParams: otherParamsDiffer,
        [PRODUCT_QUERY_PARAMS.PAGE_NUMBER]:
            params1.get(PRODUCT_QUERY_PARAMS.PAGE_NUMBER) !== params2.get(PRODUCT_QUERY_PARAMS.PAGE_NUMBER),
        [PRODUCT_QUERY_PARAMS.SORT_BY]:
            params1.get(PRODUCT_QUERY_PARAMS.SORT_BY) !== params2.get(PRODUCT_QUERY_PARAMS.SORT_BY),
    }
}
